/* ==========================================================================
   Burly — collection.js
   Collection page behavior:
   - Serializes the facets form and re-renders the section via the Section
     Rendering API (no page reloads), keeping the URL/history in sync.
   - Sort select, active-filter pills, mobile facets drawer.
   Desktop applies filters on change; the mobile drawer uses its Apply button.
   ========================================================================== */

(function () {
  'use strict';

  const root = document.querySelector('[data-collection-root]');
  if (!root) return;

  const sectionId = root.dataset.sectionId;
  const desktopQuery = window.matchMedia('(min-width: 990px)');

  /* ---- Facets drawer (mobile) ---------------------------------------- */
  const facets = document.getElementById('CollectionFacets');
  const overlay = root.querySelector('.collection__facets-overlay');

  function setDrawer(open) {
    if (!facets) return;
    facets.classList.toggle('is-open', open);
    if (overlay) overlay.classList.toggle('is-active', open);
    document.body.classList.toggle('overlay-open', open);
  }

  /* ---- AJAX rendering -------------------------------------------------- */
  function buildUrl() {
    const form = document.getElementById('FacetsForm');
    const params = new URLSearchParams();

    if (form) {
      const formData = new FormData(form);
      for (const [key, value] of formData.entries()) {
        if (String(value).trim() !== '') params.append(key, value);
      }
    } else {
      // No filters — keep existing params except page, patch sort below.
      const current = new URLSearchParams(window.location.search);
      current.delete('page');
      current.forEach((value, key) => params.append(key, value));
      params.delete('sort_by');
      const sortSelect = root.querySelector('[data-sort-select]');
      if (sortSelect) params.set('sort_by', sortSelect.value);
    }

    return window.location.pathname + '?' + params.toString();
  }

  async function renderPage(url, pushHistory) {
    root.classList.add('is-loading');
    const loading = root.querySelector('[data-loading]');
    if (loading) loading.hidden = false;

    try {
      const separator = url.includes('?') ? '&' : '?';
      const response = await fetch(url + separator + 'section_id=' + sectionId);
      const text = await response.text();
      const fresh = new DOMParser().parseFromString(text, 'text/html');

      // Swap products area (grid, toolbar, pills, pagination)
      const freshProducts = fresh.getElementById('CollectionProducts');
      const currentProducts = document.getElementById('CollectionProducts');
      if (freshProducts && currentProducts) {
        currentProducts.replaceWith(freshProducts);
      }

      // Swap facets (counts and availability change with each filter)
      const freshFacets = fresh.getElementById('CollectionFacets');
      if (freshFacets && facets) {
        const wasOpen = facets.classList.contains('is-open');
        facets.innerHTML = freshFacets.innerHTML;
        if (wasOpen) facets.classList.add('is-open');
      }

      if (pushHistory) history.pushState({ collectionUrl: url }, '', url);

      // Reveal the new cards
      if (window.Burly && Burly.initScrollReveal) Burly.initScrollReveal(root);
    } catch (error) {
      // Network hiccup — fall back to a normal navigation.
      window.location.href = url;
      return;
    }

    root.classList.remove('is-loading');
    if (loading) loading.hidden = true;
  }

  function applyFilters(pushHistory) {
    renderPage(buildUrl(), pushHistory !== false);
  }

  /* ---- Events (delegated — survive AJAX swaps) -------------------------- */
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-facets-open]')) {
      setDrawer(true);
      return;
    }
    if (event.target.closest('[data-facets-close]')) {
      setDrawer(false);
      return;
    }

    const removePill = event.target.closest('[data-facet-remove]');
    if (removePill) {
      event.preventDefault();
      renderPage(removePill.getAttribute('href'), true);
      return;
    }

    const clearLink = event.target.closest('[data-facets-clear]');
    if (clearLink) {
      event.preventDefault();
      setDrawer(false);
      renderPage(clearLink.getAttribute('href'), true);
      return;
    }

    // AJAX pagination
    const pageLink = event.target.closest('.pagination a');
    if (pageLink) {
      event.preventDefault();
      renderPage(pageLink.getAttribute('href'), true);
      const header = root.querySelector('.collection-header');
      if (header) {
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        header.scrollIntoView({ behavior: behavior, block: 'start' });
      }
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target.closest('[data-sort-select]')) {
      const sortInput = document.querySelector('[data-sort-input]');
      if (sortInput) sortInput.value = event.target.value;
      applyFilters();
      return;
    }

    // Desktop: filters apply immediately; mobile waits for the Apply button.
    if (event.target.closest('[data-facets-form]') && desktopQuery.matches) {
      applyFilters();
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-facets-form]');
    if (form) {
      event.preventDefault();
      setDrawer(false);
      applyFilters();
    }
  });

  window.addEventListener('popstate', () => {
    renderPage(window.location.pathname + window.location.search, false);
  });
})();
